/**
 * Option lists for the register column LINK DESIGNATION control ("Links to") in the
 * Configure Register Schema dialog.
 *
 * PURE presentation mapping — every value here is imported from the POI registry
 * (`@/features/Dashboard/utils/poiResolvers` / `poiConfig`), never re-typed as a literal. The
 * designation is written verbatim into the register's `SchemaJson`
 * (`RegisterColumnDefinition.LinkEntityType` / `LinkKeyField`) and read back by the dashboard, so a
 * value invented here would silently resolve to nothing. This module owns ONLY the labels.
 *
 * Offered types come from `POI_RESOLVERS` — i.e. exactly the types that can actually be resolved.
 * That deliberately excludes `masterTag`, which the registry declares but has no resolver for (the
 * whole-tag path already covers it).
 */
import { POI_ENTITY_TYPES, POI_RESOLVERS, DEFAULT_KEY_FIELD, SUPPORTED_KEY_FIELDS } from '@/features/Dashboard/utils/poiResolvers';
import { POI_HIGHLIGHT_TYPES, POI_CONTEXT_TYPES, POI_HIGHLIGHT_DISABLED_REASON } from '@/features/Dashboard/utils/poiConfig';

/** Human labels for the entity types, keyed by the stored value. */
const ENTITY_TYPE_LABEL = Object.freeze({
  [POI_ENTITY_TYPES.CML]: 'CML',
  [POI_ENTITY_TYPES.ANOMALY_TAG]: 'Anomaly tag',
  [POI_ENTITY_TYPES.INSPECTION_TAG]: 'Inspection tag',
  [POI_ENTITY_TYPES.DOCUMENT]: 'Document',
});

/** The "no link" choice. `null` (not '') so it serialises to a cleared declaration. */
export const LINK_ENTITY_TYPE_NONE = null;

/**
 * `v-select` items for the "Links to" picker: None first, then every resolvable entity type.
 * @type {ReadonlyArray<{text:string, value:?string}>}
 */
export const LINK_ENTITY_TYPE_OPTIONS = Object.freeze([
  { text: 'None', value: LINK_ENTITY_TYPE_NONE },
  ...Object.keys(POI_RESOLVERS).map((type) => ({
    text: ENTITY_TYPE_LABEL[type] || type,
    value: type,
  })),
]);

/**
 * The default key field for a type — what an absent `LinkKeyField` means at resolve time.
 * @param {?string} entityType
 * @returns {string|null}
 */
export function defaultKeyFieldFor(entityType) {
  if (!entityType) return null;
  return DEFAULT_KEY_FIELD[entityType] ?? null;
}

/**
 * `v-select` items for the key field of a designated column — only the fields the type's resolver
 * can match on, default first and labelled as such.
 * @param {?string} entityType
 * @returns {Array<{text:string, value:string}>}
 */
export function keyFieldOptions(entityType) {
  const fields = SUPPORTED_KEY_FIELDS[entityType];
  if (!fields) return [];
  const defaultField = defaultKeyFieldFor(entityType);
  return fields.map((field) => ({
    text: field === defaultField ? `${field} (default)` : field,
    value: field,
  }));
}

/**
 * True when a type can drive the 3D HIGHLIGHT (a narrow anchor). `inspectionTag` covers the whole
 * asset and `document` has no geometry, so they may only be the CONTEXT.
 * @param {?string} entityType
 * @returns {boolean}
 */
export function canHighlight(entityType) {
  return !!entityType && POI_HIGHLIGHT_TYPES.includes(entityType);
}

/**
 * True when a type can be the CONTEXT — the backdrop a highlight is shown over. Everything with
 * geometry qualifies; `document` does not, because there is nothing to dim.
 * @param {?string} entityType
 * @returns {boolean}
 */
export function canContext(entityType) {
  return !!entityType && POI_CONTEXT_TYPES.includes(entityType);
}

/** The register-level default a designated column may carry (at most one column per register each). */
export const LINK_DEFAULT_ROLE = Object.freeze({
  NONE: 'none',
  HIGHLIGHT: 'highlight',
  CONTEXT: 'context',
});

/**
 * `v-select` items for a column's register-level default role. A role the declared type cannot play
 * is offered DISABLED — carrying the registry's own reason where it has one — so the user gets an
 * explanation instead of a silently missing option.
 * @param {?string} entityType
 * @returns {Array<{text:string, value:string, disabled?:boolean}>}
 */
export function defaultRoleOptions(entityType) {
  // The registry keys its reasons by why a type cannot NARROW; the no-geometry one ('document')
  // is equally the reason it cannot be a context either.
  const blockedReason = POI_HIGHLIGHT_DISABLED_REASON[entityType];
  const contextBlocked = !canContext(entityType);
  return [
    { text: 'No default', value: LINK_DEFAULT_ROLE.NONE },
    {
      text: blockedReason ? `Highlight — ${blockedReason}` : 'Default highlight',
      value: LINK_DEFAULT_ROLE.HIGHLIGHT,
      disabled: !canHighlight(entityType),
    },
    {
      text: contextBlocked && blockedReason ? `Context — ${blockedReason}` : 'Default context',
      value: LINK_DEFAULT_ROLE.CONTEXT,
      disabled: contextBlocked,
    },
  ];
}

/**
 * Collapse the two stored boolean flags to the single role the picker shows.
 * @param {{isDefaultHighlight:?boolean, isDefaultContext:?boolean}} link
 * @returns {string} one of LINK_DEFAULT_ROLE
 */
export function roleFromFlags(link) {
  if (link?.isDefaultHighlight) return LINK_DEFAULT_ROLE.HIGHLIGHT;
  if (link?.isDefaultContext) return LINK_DEFAULT_ROLE.CONTEXT;
  return LINK_DEFAULT_ROLE.NONE;
}

/**
 * Expand the picked role back to the two flags the API command carries. They are mutually exclusive
 * — the server rejects a column that claims both.
 * @param {string} role - one of LINK_DEFAULT_ROLE
 * @returns {{isDefaultHighlight:boolean, isDefaultContext:boolean}}
 */
export function flagsFromRole(role) {
  return {
    isDefaultHighlight: role === LINK_DEFAULT_ROLE.HIGHLIGHT,
    isDefaultContext: role === LINK_DEFAULT_ROLE.CONTEXT,
  };
}
